import React from "react"
import { ShieldCheck, Eye, Calendar, Heart } from "lucide-react"
import TikTokPlayer from "./TikTokPlayer"
import Avatar from "./Avatar"
import CaptionText from "./CaptionText"
import { formatViews } from "../utils"

/**
 * TikTok-only grid tile - same "real preview up front" idea as ReelCard, but
 * TikTok's payload has no confirmed playable file (see TikTokPlayer.jsx), so
 * the preview is a still frame with a permanent "Watch on TikTok" overlay
 * instead of an inline <video>.
 */
const TikTokCard = ({ video, onOpen }) => (
    <div style={{
        background: "#1c1d27",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "16px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
    }}>
        <div style={{ position: "relative", width: "100%", aspectRatio: "9 / 16", background: "#000" }}>
            <TikTokPlayer video={video} onOpen={() => onOpen(video)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />

            <div style={{ position: "absolute", top: "8px", left: "8px", display: "flex", gap: "6px", pointerEvents: "none" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#000", color: "#25F4EE", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "5px" }}>
                    TIKTOK
                </span>
                {video.verification?.verified && (
                    <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "rgba(16,185,129,0.9)", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "5px" }}>
                        <ShieldCheck size={11} /> Verified
                    </span>
                )}
            </div>

            {video.duration_text && (
                <span style={{ position: "absolute", bottom: "8px", right: "8px", background: "rgba(0,0,0,0.8)", color: "#fff", fontSize: "11px", fontWeight: 600, padding: "2px 6px", borderRadius: "5px", pointerEvents: "none" }}>
                    {video.duration_text}
                </span>
            )}
        </div>

        {/* The overlay above is the preview + watch-out link; this footer is
            the click target for the full detail view (summary, verification
            reasoning, etc.) - it never sits on top of the overlay's own pill. */}
        <div onClick={() => onOpen(video)} style={{ padding: "12px 14px 14px", cursor: "pointer", display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
            <h5
                className="ugc-clamp-2"
                style={{ color: "#fff", fontSize: "13.5px", fontWeight: 600, margin: 0, lineHeight: "1.4", fontFamily: "Rubik" }}
                title={video.title}
            >
                <CaptionText text={video.title} />
            </h5>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "11.5px", color: "#9ca3af", flexWrap: "wrap" }}>
                <Avatar src={video.profile_pic_url} alt={video.channel_name} size={16} />
                <span style={{ fontWeight: 500 }}>{video.channel_name}</span>
                <span>•</span>
                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Eye size={11} /> {formatViews(video.metrics?.views)}</span>
                {/* Shows likes rather than `saves` (TikTok-only) here, matching
                    VideoCard.jsx's detail view - likes is the more familiar,
                    immediately-understood number for a grid tile. Can be null
                    for a given video even though views/saves are present -
                    TikTok's search API sometimes omits it per-video
                    (TIKTOK_VIDEO_API.md §"metrics"), not a bug. */}
                {video.metrics?.likes != null && (
                    <>
                        <span>•</span>
                        <span title="Likes" style={{ display: "flex", alignItems: "center", gap: "4px" }}><Heart size={11} /> {formatViews(video.metrics.likes)}</span>
                    </>
                )}
                <span>•</span>
                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Calendar size={11} /> {video.published_time_text}</span>
            </div>
        </div>
    </div>
)

export default TikTokCard
